import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import type { AlteracaoDoPrazo, ListaDePrazos, PrazoDoSistema } from './contrato';

/**
 * A camada de dados dos prazos parametrizáveis do sistema. Dado real, da API
 * (`bff/v1/admin/prazos-sistema`); a regra — prazo que uma função do banco lê não
 * se desativa, e a unidade de quem lê o valor cru não muda — é conferida no banco
 * (migração 0321).
 *
 * Nenhum componente chama `chamarApi` direto, a chave de cache mora junto de quem
 * a usa, e toda consulta leva `retry: naoInsistirEmRecusa` (CLAUDE.md, "Regras de
 * API").
 */
export const CAMINHO_DA_API = '/bff/v1/admin/prazos-sistema';

/**
 * O catálogo é pequeno e a tela o mostra agrupado por categoria, de uma vez: pede
 * a página máxima que a API aceita.
 */
export const TAMANHO_DA_PAGINA = 200;

export const CHAVES = {
  lista: ['admin', 'prazos-sistema', 'lista'] as const,
  tudo: ['admin', 'prazos-sistema'] as const,
};

export function usePrazosDoSistema() {
  return useQuery({
    queryKey: CHAVES.lista,
    queryFn: ({ signal }) =>
      chamarApi<ListaDePrazos>(`${CAMINHO_DA_API}?pagina=1&tamanho=${TAMANHO_DA_PAGINA}`, {
        sinal: signal,
      }),
    retry: naoInsistirEmRecusa,
  });
}

/**
 * A alteração devolve o prazo gravado. A lista é invalidada também na falha: a
 * recusa mais comum é a edição desatualizada, e a lista nova traz a versão certa.
 */
export function useAlterarPrazo(id: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (alteracao: AlteracaoDoPrazo) =>
      chamarApi<PrazoDoSistema>(`${CAMINHO_DA_API}/${encodeURIComponent(id)}`, {
        metodo: 'PUT',
        corpo: alteracao,
      }),
    onSettled: () => cliente.invalidateQueries({ queryKey: CHAVES.tudo }),
  });
}

/** Mensagem da API quando ela disse o que fazer; senão, a genérica. */
export function mensagemDoErro(erro: unknown, generica: string): string {
  return erro instanceof ErroApi && erro.message ? erro.message : generica;
}
